import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, MapPin, Plus, X } from 'lucide-react';
import { useDiveSiteSearch, useDiveSitesNearby } from '@/data/dive-sites';
import type { DiveSite } from '@/types/api';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { QuickCreateDivesiteForm } from './QuickCreateDivesiteForm';
import { cn } from '@/lib/utils';

interface DivesitePickerProps {
  value: DiveSite | null;
  onChange: (site: DiveSite) => void;
  /** Initial nearby search (e.g. from the user's last dive). */
  defaultCoords?: { lat: number; lng: number } | null;
}

export function DivesitePicker({ value, onChange, defaultCoords = null }: DivesitePickerProps) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [browserCoords, setBrowserCoords] = useState<{ lat: number; lng: number } | null>(defaultCoords);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Debounce search query
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 200);
    return () => clearTimeout(t);
  }, [q]);

  // Optional: ask for browser geo on first open
  useEffect(() => {
    if (open && !browserCoords && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setBrowserCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {/* silently ignore */},
        { enableHighAccuracy: false, timeout: 3_000, maximumAge: 60 * 60 * 1000 },
      );
    }
  }, [open, browserCoords]);

  // Close popover on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false);
        setShowCreate(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const searchQuery = useDiveSiteSearch(debouncedQ, open && debouncedQ.length >= 2);
  const nearbyQuery = useDiveSitesNearby(browserCoords ? { lat: browserCoords.lat, lon: browserCoords.lng } : null, 25, 12);

  const showNearby = open && debouncedQ.length < 2 && (nearbyQuery.data?.items.length ?? 0) > 0;
  const results: DiveSite[] = useMemo(() => {
    if (debouncedQ.length >= 2) return searchQuery.data?.items ?? [];
    return nearbyQuery.data?.items ?? [];
  }, [debouncedQ, searchQuery.data, nearbyQuery.data]);

  const handleSelect = (site: DiveSite) => {
    onChange(site);
    setOpen(false);
    setShowCreate(false);
    setQ('');
  };

  return (
    <div className="relative" ref={popoverRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex h-10 w-full items-center justify-between rounded-md border border-[var(--border)] bg-[var(--input)] px-3 text-left text-sm',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]',
        )}
      >
        <span className="truncate">
          {value ? (
            <span className="flex items-center gap-2">
              <MapPin className="h-3.5 w-3.5 text-ocean-700" />
              <span>{value.label}</span>
            </span>
          ) : (
            <span className="text-[var(--muted-foreground)]">Tauchplatz auswählen …</span>
          )}
        </span>
        <Search className="h-4 w-4 text-[var(--muted-foreground)]" />
      </button>

      {open && (
        <div className="absolute z-50 mt-2 w-full overflow-hidden rounded-lg border border-[var(--border)] bg-[var(--card)] shadow-xl">
          {showCreate ? (
            <div className="p-4">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-semibold">Neuen Tauchplatz anlegen</h3>
                <button
                  type="button"
                  onClick={() => setShowCreate(false)}
                  className="rounded p-1 text-[var(--muted-foreground)] hover:bg-[var(--muted)]"
                  aria-label="Schließen"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <QuickCreateDivesiteForm
                initialQuery={debouncedQ || q}
                initialCoords={browserCoords}
                onCreated={handleSelect}
                onCancel={() => setShowCreate(false)}
              />
            </div>
          ) : (
            <>
              <div className="relative border-b border-[var(--border)] p-2">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--muted-foreground)]" />
                <Input
                  autoFocus
                  type="search"
                  autoComplete="off"
                  data-1p-ignore="true"
                  data-lpignore="true"
                  data-bwignore="true"
                  placeholder="Site, Region…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="border-0 bg-transparent pl-9 focus-visible:ring-0"
                />
              </div>

              <div className="max-h-72 overflow-auto">
                {(searchQuery.isFetching || nearbyQuery.isFetching) && (
                  <div className="grid place-items-center py-4"><Spinner /></div>
                )}

                {showNearby && (
                  <p className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-[var(--muted-foreground)]">
                    In deiner Nähe
                  </p>
                )}

                {results.length === 0 && debouncedQ.length >= 2 && !searchQuery.isFetching && (
                  <p className="px-3 py-4 text-center text-sm text-[var(--muted-foreground)]">
                    Keine Treffer für „{debouncedQ}".
                  </p>
                )}

                <ul>
                  {results.map((site) => (
                    <li key={site.id}>
                      <button
                        type="button"
                        onClick={() => handleSelect(site)}
                        className="flex w-full items-start gap-3 px-3 py-2 text-left hover:bg-[var(--muted)]"
                      >
                        <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-ocean-700" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">{site.title}</p>
                          {site.regionPathStr && (
                            <p className="truncate text-xs text-[var(--muted-foreground)]">{site.regionPathStr}</p>
                          )}
                        </div>
                        {site.distanceMeters !== undefined && (
                          <span className="shrink-0 text-xs text-[var(--muted-foreground)]">
                            {formatDistance(site.distanceMeters)}
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              <button
                type="button"
                onClick={() => setShowCreate(true)}
                className="flex w-full items-center gap-2 border-t border-[var(--border)] bg-ocean-50 px-3 py-3 text-sm font-medium text-ocean-700 hover:bg-ocean-100 dark:bg-ocean-900/50 dark:text-ocean-200 dark:hover:bg-ocean-900"
              >
                <Plus className="h-4 w-4" />
                <span>{debouncedQ ? `„${debouncedQ}" als neuen Tauchplatz anlegen` : 'Neuen Tauchplatz anlegen'}</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function formatDistance(meters: number): string {
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(1)} km`;
}
